import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'

// PrimeNG
import { BadgeModule } from 'primeng/badge'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { SkeletonModule } from 'primeng/skeleton'
import { PopoverModule } from 'primeng/popover'

// FontAwesome
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { faLocationDot, faRoute } from '@fortawesome/free-solid-svg-icons'

// Tools
import { TechnicianTools } from '../../../core/tools/technician-tools'

// Services - using shared type
import type { SlotIndicator } from '../../../modules/affairs/services'

@Component({
  selector: 'app-closest-interventions-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BadgeModule,
    ProgressSpinnerModule,
    SkeletonModule,
    PopoverModule,
    FontAwesomeModule,
  ],
  templateUrl: './closest-interventions-list.component.html',
  styleUrls: ['./closest-interventions-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClosestInterventionsListComponent {
  // Icons
  public faLocationDot = faLocationDot
  public faRoute = faRoute

  // State
  public hoveredInterventionId: number | null = null

  // Inputs
  @Input() interventions: any[] = []
  @Input() selectedIntervention: any = null
  @Input() loadingInterventions = false
  @Input() loadingBatchIndicators = false
  @Input() loadingSlotTravelInfo = false
  @Input() slotTravelInfo: Map<number, any> = new Map()
  @Input() interventionSlotIndicators: Map<number, SlotIndicator> = new Map()
  @Input() sortBy: 'distance' | 'date' = 'distance'
  @Input() limit = 5
  @Input() showHeader = true
  @Input() showControls = true

  // Outputs
  @Output() interventionSelected = new EventEmitter<any>()
  @Output() interventionMouseEnter = new EventEmitter<any>()
  @Output() interventionMouseLeave = new EventEmitter<void>()
  @Output() refreshClicked = new EventEmitter<void>()
  @Output() sortByChanged = new EventEmitter<'distance' | 'date'>()
  @Output() limitChanged = new EventEmitter<number>()

  // Methods
  getTechnicianInitials(technician: any): string {
    return TechnicianTools.getInitials(technician)
  }

  onInterventionClick(intervention: any): void {
    this.interventionSelected.emit(intervention)
  }

  onMouseEnter(intervention: any): void {
    this.interventionMouseEnter.emit(intervention)
  }

  onMouseLeave(): void {
    this.interventionMouseLeave.emit()
  }

  onRefresh(): void {
    this.refreshClicked.emit()
  }

  onSortByChange(sortBy: 'distance' | 'date'): void {
    this.sortByChanged.emit(sortBy)
  }

  onLimitChange(limit: number): void {
    this.limitChanged.emit(limit)
  }

  getInterventionSlotIndicator(interventionId: number): SlotIndicator | null {
    return this.interventionSlotIndicators.get(interventionId) || null
  }

  /**
   * Get feasible slots count for selected intervention from slotTravelInfo
   * Excludes shift-slots which have their own category
   */
  getFeasibleSlotsCount(): number {
    if (this.slotTravelInfo.size === 0) return 0
    let count = 0
    this.slotTravelInfo.forEach((info) => {
      // Exclure les shift-slots - ils ont leur propre catégorie "décaler"
      if (info.requiresShift) return
      if (info.isFeasible) count++
    })
    return count
  }

  /**
   * Get adjustable slots count for selected intervention from slotTravelInfo
   * Only counts slots that can actually be adjusted (not blocked by do_not_shift)
   */
  getAdjustableSlotsCount(): number {
    if (this.slotTravelInfo.size === 0) return 0
    let count = 0
    this.slotTravelInfo.forEach((info) => {
      if (!info.isFeasible && info.needsAdjustment) {
        // Check if the slot can be adjusted (not blocked by do_not_shift)
        if (
          info.previousEventDoNotShift !== true &&
          info.nextEventDoNotShift !== true
        ) {
          count++
        }
      }
    })
    return count
  }

  /**
   * Get blocked slots count (need adjustment but cannot be adjusted due to do_not_shift)
   */
  getBlockedSlotsCount(): number {
    if (this.slotTravelInfo.size === 0) return 0
    let count = 0
    this.slotTravelInfo.forEach((info) => {
      if (!info.isFeasible && info.needsAdjustment) {
        // Check if the slot is blocked by do_not_shift
        if (
          info.previousEventDoNotShift === true ||
          info.nextEventDoNotShift === true
        ) {
          count++
        }
      }
    })
    return count
  }

  /**
   * Get total available slots for an intervention
   */
  getAvailableSlotsCount(intervention: any): number {
    const indicator = this.getInterventionSlotIndicator(intervention.id)
    return indicator?.totalSlots || 0
  }

  trackByInterventionId(index: number, intervention: any): any {
    return intervention.id || index
  }

  trackByTechnicianId(index: number, technician: any): any {
    return technician.id || technician.email || index
  }
}
